import React, { useEffect, useMemo, useState } from 'react';

import { Table } from 'antd';
import Axios from 'axios';

import { capitalize } from '@libs/NwUtils';

import { useUserGuiConfig } from '@contexts/UserGuiConfigContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { forceReloadProject } from '@hooks/API/Project/UseProject';
import { forceReloadProjectTransactions } from '@hooks/API/Project/UseProjectTransactions';
import { forceReloadInvoices } from '@hooks/API/UseInvoices';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import EmptyState from '@components/Gui/EmptyState';
import Money from '@components/Gui/Money';
import NwIcon from '@components/Gui/NwIcon';
import {
    buildInvoiceDraftData,
    countAgencyFeeStatements,
    getUniqueBillingInfoIds,
    hasMixedCurrenciesForInvoice,
    resolveSelectedCurrencyFromStatements,
    sumTaxedStatementsTotal,
} from '@components/StatementRowsCommon/helpers';
import { adaptReceivableStatements } from '@components/StatementRowsCommon/adapters';

import { StatementDrawers } from '../StatementCommon';
import { useStatementContext, useStatementDispatch } from '../StatementContext';
import { StatementTableContainer } from '../StatementUI';
import ReceivableActions from './ReceivableActions';
import { getReceivableColumns } from './ReceivableColumns';
import { getTableClassName, getRowClassName, isStatementPaid } from './ReceivableHelpers';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/**
 * ReceivableBody
 *
 * Table body component for receivable statements.
 * Displays statements (not transactions) with multi-selection and invoice creation.
 */
const ReceivableBody = ({ isPrevisional, transactions, statementTotals, afterStatementReload, hideTotal }) => {
    const userGuiConfig = useUserGuiConfig();
    const configuration = useConfigurations();
    let numberformat = 'USD0,0.00';
    if (userGuiConfig && userGuiConfig.numbers && userGuiConfig.numbers.format) {
        numberformat = userGuiConfig.numbers.format;
    }

    const { contactID, contactType, expand, showSubjectNameInTitle, scope } = useStatementContext();
    const dispatch = useStatementDispatch();

    const onStatementReload = () => {
        dispatch({ type: 'StartReload' });
        if (afterStatementReload) {
            afterStatementReload();
        }
    };

    // State for drawers and selections
    const [selectedTransaction, setSelectedTransaction] = useState();
    const [showTransactionDetailDrawer, setShowTransactionDetailDrawer] = useState(false);
    const [docVersion, setDocVersion] = useState();
    const [invoiceData, setInvoiceData] = useState();
    const [showInvoiceDrawer, setShowInvoiceDrawer] = useState(false);
    const [addToInvoiceData, setAddToInvoiceData] = useState(null);
    const [selectedList, setSelectedList] = useState([]);
    const [disableInvoiceButton, setDisableInvoiceButton] = useState(true);
    const [invoiceButtonErrors, setInvoiceButtonErrors] = useState([]);
    const [serviceId, setServiceId] = useState();
    const [isServiceDrawer, setIsServiceDrawer] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [projectPreviewID, setProjectPreviewID] = useState();
    const [expandedItems, setExpandedItems] = useState([]);
    const [transactionWasModified, setTransactionWasModified] = useState(false);
    const [selectedStatementsTotal, setSelectedStatementsTotal] = useState('');

    const configurations = useConfigurations();

    const handleCloseInvoiceDrawer = () => {
        onStatementReload();
        setSelectedInvoice(null);
    };

    useEffect(() => {
        setSelectedList([]);
    }, [transactions]);

    const isItemExpanded = (itemID) => {
        return expand || expandedItems.includes(itemID);
    };

    const clearSelectedList = () => {
        setSelectedList([]);
        setSelectedStatementsTotal('');
    };

    const handleInvoiceCreation = (invoiceID) => {
        forceReloadInvoices(contactID);
        setShowInvoiceDrawer(false);
        setInvoiceData(null);
        clearSelectedList();
        setSelectedInvoice(invoiceID);
    };

    const handleRowsAddedToInvoice = (invoiceID) => {
        forceReloadInvoices(contactID);
        setAddToInvoiceData(null);
        clearSelectedList();
        setSelectedInvoice(invoiceID);
    };

    const handleCloseAddToInvoiceDrawer = (someRowsAdded) => {
        setAddToInvoiceData(null);
        if (someRowsAdded) {
            forceReloadInvoices(contactID);
            onStatementReload();
        }
    };

    const handleCloseDetailDrawer = () => {
        setShowTransactionDetailDrawer(false);
        setSelectedTransaction(null);
        if (transactionWasModified) {
            onStatementReload();
            setTransactionWasModified(false);
        }
    };

    const handleEditTransaction = (transaction) => {
        setSelectedTransaction(transaction);
        setShowTransactionDetailDrawer(true);
        setTransactionWasModified(false);
    };

    const handlePreviewProject = (projectID) => {
        setProjectPreviewID(projectID);
    };

    const handleCloseProjectDrawer = () => {
        onStatementReload();
        setProjectPreviewID();
    };

    const handlePreviewService = (serviceID) => {
        setServiceId(serviceID);
        setIsServiceDrawer(true);
    };

    const handleCloseServiceDrawer = () => {
        setServiceId();
        setIsServiceDrawer(false);
    };

    const toggleItemExpansion = (itemID) => {
        if (!expand) {
            if (!expandedItems.includes(itemID)) {
                setExpandedItems([...expandedItems, itemID]);
            } else {
                setExpandedItems(expandedItems.filter((id) => id !== itemID));
            }
        }
    };

    // Get columns configuration
    const columns = getReceivableColumns({
        contactType,
        showSubjectNameInTitle,
        expand,
        isItemExpanded,
        handleEditTransaction,
        handlePreviewProject,
        handlePreviewService,
        onStatusInvoiceClose: onStatementReload,
    });

    const displayedStatements = useMemo(() => adaptReceivableStatements(transactions), [transactions]);

    const handleCreateInvoice = async () => {
        setShowInvoiceDrawer(false);
        const selectedStatements = displayedStatements.filter((st) => selectedList.includes(st.ID));
        const uniquebillinginfosIDs = getUniqueBillingInfoIds(selectedStatements);
        if (uniquebillinginfosIDs.length !== 1) {
            alert('Please select statements with the same billing info');
            return;
        }

        const contactsBillingInfos = await Axios.get(`/contacts/billinginfos/${contactID}`);

        if (!contactsBillingInfos || contactsBillingInfos.length === 0) {
            alert('No billing info found for this contact');
            return;
        }

        const selectedBillingInfo = contactsBillingInfos.find((bi) => bi.ID === uniquebillinginfosIDs[0]);

        if (!selectedBillingInfo) {
            alert('Selected billing info not found');
            return;
        }

        const ret = buildInvoiceDraftData({
            statements: selectedStatements,
            contactID,
            contactName: getContactName(contactID),
            contactTypeLabel: capitalize(contactType),
            billingInfo: selectedBillingInfo,
            defaultCurrency: configurations.defaultCurrency,
            agencyFeeCount: countAgencyFeeStatements(selectedStatements),
        });

        setInvoiceData(ret);
        setShowInvoiceDrawer(true);
    };

    // Same billing info and currency rules as invoice creation, applied to an existing draft invoice
    const handleAddToInvoice = () => {
        const selectedStatements = displayedStatements.filter((st) => selectedList.includes(st.ID));
        const uniquebillinginfosIDs = getUniqueBillingInfoIds(selectedStatements);
        if (uniquebillinginfosIDs.length !== 1) {
            alert('Please select statements with the same billing info');
            return;
        }

        setAddToInvoiceData({
            ContactID: contactID,
            Type: capitalize(contactType),
            BillingInfoID: uniquebillinginfosIDs[0],
            Currency: resolveSelectedCurrencyFromStatements(selectedStatements, configurations.defaultCurrency),
            HasMixedCurrencies: hasMixedCurrenciesForInvoice(selectedStatements, configurations.defaultCurrency),
            Rows: selectedStatements.map((st) => ({ StatementID: st.ID, Title: st.Transaction.Title })),
        });
    };

    const checkSelectedRows = (selectedRows) => {
        let disableInvoice = false;

        if (selectedRows.length) {
            const defCur = configuration.defaultCurrency;
            setSelectedStatementsTotal(sumTaxedStatementsTotal(selectedRows, defCur, numberformat));
        } else {
            setSelectedStatementsTotal('');
        }

        const invoiceErrors = [];

        for (const st of selectedRows) {
            if (st.InvoiceID) {
                invoiceErrors.push(`Line "${st.Transaction.Title}" is already invoiced`);
                disableInvoice = true;
            }
        }

        // Same currency rule as billing and add to invoice: one invoice, one currency
        if (hasMixedCurrenciesForInvoice(selectedRows, configuration.defaultCurrency)) {
            invoiceErrors.push('Selected lines have different currencies: convert them to the default currency');
            disableInvoice = true;
        }

        setInvoiceButtonErrors(invoiceErrors);
        setDisableInvoiceButton(disableInvoice);
    };

    const rowSelection = {
        selectedRowKeys: selectedList,
        onChange: (selectedRowKeys, selectedRows) => {
            checkSelectedRows(selectedRows);
            setSelectedList(selectedRowKeys);
        },
        getCheckboxProps: (record) => {
            return {
                disabled: isPrevisional || !!record.InvoiceID,
            };
        },
    };

    return (
        <StatementTableContainer $search={scope === 'search'}>
            {displayedStatements.length > 0 ? (
                <>
                    <Table
                        rowSelection={scope === 'statement' ? rowSelection : undefined}
                        showSizeChanger
                        pagination={false}
                        columns={columns}
                        dataSource={displayedStatements}
                        className={getTableClassName(isPrevisional)}
                        rowClassName={(record) => {
                            let cn = getRowClassName(record, isStatementPaid);
                            if (record.isCommission) {
                                cn += ' commission-row';
                            }
                            if (record.isClientAdvance) {
                                cn += ' advance-row';
                            }
                            if (record.HasCommission) {
                                cn += ' fee-row';
                            }
                            if (record.Transaction.WorkDateCancelled) {
                                cn += ' cancelled';
                            }
                            cn += ' cursor-pointer';
                            return cn;
                        }}
                        summary={(pageData) => {
                            if (hideTotal) {
                                return null;
                            }
                            // Calculate colspan based on number of columns
                            // Total columns: [Checkbox], Date, Client Status, Title, Project, [Job Amount if not agency], Client Amount, Actions
                            // Summary needs: colspan cells + "Total" cell + "Client Amount" cell + "Actions" cell
                            let summaryColSpan = 3; // Date, Client Status, Title, Project
                            if (contactType !== 'agency') {
                                summaryColSpan += 1; // Job Amount
                            }
                            if (scope === 'statement') {
                                summaryColSpan += 1; // Checkbox column
                            }

                            return (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell colSpan={summaryColSpan}></Table.Summary.Cell>
                                    <Table.Summary.Cell>
                                        <strong style={{ display: 'block', textAlign: 'right' }}>Total</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell>
                                        {statementTotals.map((item) => (
                                            <Money
                                                key={item.Currency}
                                                dineroObj={item.Dinero}
                                                redIfNegative
                                                boldIfDefaultCurrency
                                                style={{ display: 'block', textAlign: 'right' }}
                                            />
                                        ))}
                                        <small style={{ display: 'block', textAlign: 'right', fontSize: '0.75rem' }}>
                                            DISPLAYED ROWS SUBTOTAL
                                        </small>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell></Table.Summary.Cell>
                                </Table.Summary.Row>
                            );
                        }}
                    />
                    {scope === 'statement' && selectedList.length > 0 && (
                        <ReceivableActions
                            selectedList={selectedList}
                            disableInvoiceButton={disableInvoiceButton}
                            invoiceButtonErrors={invoiceButtonErrors}
                            selectedStatementsTotal={selectedStatementsTotal}
                            handleCreateInvoice={handleCreateInvoice}
                            handleAddToInvoice={handleAddToInvoice}
                        />
                    )}
                </>
            ) : (
                <EmptyState message="No Results" />
            )}
            <StatementDrawers
                showTransactionDetailDrawer={showTransactionDetailDrawer}
                selectedTransaction={selectedTransaction}
                handleCloseDetailDrawer={handleCloseDetailDrawer}
                onUpdateTransaction={() => {
                    if (scope !== 'search') {
                        onStatementReload();
                    } else {
                        setTransactionWasModified(true);
                    }
                }}
                onTransactionRemoved={() => {
                    setShowTransactionDetailDrawer(false);
                    setSelectedTransaction(null);
                    onStatementReload();
                    if (selectedTransaction.Project?.ID) {
                        forceReloadProjectTransactions(selectedTransaction.Project.ID);
                        forceReloadProject(selectedTransaction.Project.ID);
                    }
                }}
                docVersion={docVersion}
                setDocVersion={setDocVersion}
                isServiceDrawer={isServiceDrawer}
                serviceId={serviceId}
                handleCloseServiceDrawer={handleCloseServiceDrawer}
                showPaymentDrawer={false}
                setShowPaymentDrawer={() => {}}
                paymentData={null}
                handlePaymentCreation={() => {}}
                selectedPayment={null}
                handleClosePaymentDrawer={() => {}}
                contactType="client"
                showInvoiceDrawer={showInvoiceDrawer}
                setShowInvoiceDrawer={setShowInvoiceDrawer}
                invoiceData={invoiceData}
                handleInvoiceCreation={handleInvoiceCreation}
                selectedInvoice={selectedInvoice}
                handleCloseInvoiceDrawer={handleCloseInvoiceDrawer}
                addToInvoiceData={addToInvoiceData}
                handleCloseAddToInvoiceDrawer={handleCloseAddToInvoiceDrawer}
                handleRowsAddedToInvoice={handleRowsAddedToInvoice}
                projectPreviewID={projectPreviewID}
                handleCloseProjectDrawer={handleCloseProjectDrawer}
            />
        </StatementTableContainer>
    );
};

export default ReceivableBody;
